import classNames from 'classnames';
import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef } from 'react';
import { Portal } from 'shared/ui/Portal';

import styles from './Modal.module.scss';

export interface ModalProps {
  className?: string;
  classNameOverlay?: string;
  classNameWrapper?: string;
  children?: ReactNode;
  isOpen?: boolean;
  isWrapperContainer?: boolean;
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
}

export const Modal = ({
  className,
  classNameOverlay,
  classNameWrapper,
  children,
  isWrapperContainer = true,
  isOpen,
  onClose,
  closeOnOverlayClick,
}: ModalProps) => {
  const ContainerRef = useRef();
  const overlay = useRef();
  const isMounted = useRef<boolean>(false);
  const onKeyDown = useCallback(
    (e: KeyboardEvent & Event) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('lock');
      window.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.body.classList.remove('lock');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMounted, isOpen, onKeyDown]);

  if (isOpen === false) {
    return null;
  }
  return (
    <Portal>
      <div className={classNames(styles.WrapperModal, { [styles.opened]: isOpen }, classNameWrapper)}>
        <div
          className={classNames(styles.Overlay, classNameOverlay)}
          ref={overlay}
          onMouseDown={e => {
            closeOnOverlayClick && isMounted && e.target === overlay.current && onClose();
          }}
        >
          {isWrapperContainer ? (
            <div ref={ContainerRef} className={classNames(styles.Content, className)}>
              {children}
            </div>
          ) : (
            <>{children}</>
          )}
        </div>
      </div>
    </Portal>
  );
};
